/**
 * SEO del módulo de Subvenciones.
 *
 * Centraliza slugs (comunidad / sector), plantillas de title + meta
 * description y el JSON-LD GovernmentService de la ficha. Todo bilingüe.
 */
import { COMUNIDADES, isAmbitoGlobal, matchesComunidad } from "@/lib/subvenciones";
import { RETAIL_SECTORS } from "@/components/SectorOnboardingModal";

export const slugify = (v: string): string =>
  v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** Comunidades indexables: slug → nombre oficial. */
export const COMUNIDAD_SLUGS: { slug: string; name: string }[] = COMUNIDADES.map((c) => ({
  slug: slugify(c),
  name: c,
}));

export const comunidadFromSlug = (slug?: string | null): string | null =>
  COMUNIDAD_SLUGS.find((c) => c.slug === (slug ?? "").toLowerCase())?.name ?? null;

/** Sectores indexables (etiqueta del monitor): slug → label. */
export const SECTOR_SLUGS: { slug: string; label: string }[] = RETAIL_SECTORS.filter(
  (s) => s.key !== "otro",
).map((s) => ({ slug: slugify(s.label), label: s.label }));

export const sectorFromSlug = (slug?: string | null): string | null =>
  SECTOR_SLUGS.find((s) => s.slug === (slug ?? "").toLowerCase())?.label ?? null;

export const grantsBase = (lang: "es" | "en") => (lang === "es" ? "/subvenciones" : "/en/grants");
export const territorySegment = (lang: "es" | "en") => (lang === "es" ? "territorio" : "region");

export const territoryPath = (lang: "es" | "en", slug: string) =>
  `${grantsBase(lang)}/${territorySegment(lang)}/${slug}`;
export const sectorPath = (lang: "es" | "en", slug: string) => `${grantsBase(lang)}/sector/${slug}`;

// ── Slug de ficha (código + título) ──────────────────────────────────────
/** Convierte el título de la convocatoria en slug corto (máx. ~70 chars, sin cortar palabras). */
export const grantSlugFromTitulo = (titulo?: string | null, maxLen = 70): string => {
  const base = slugify(titulo ?? "");
  if (base.length <= maxLen) return base;
  const cut = base.slice(0, maxLen);
  const lastDash = cut.lastIndexOf("-");
  return (lastDash > 20 ? cut.slice(0, lastDash) : cut).replace(/-$/, "");
};

/** Path canónico de la ficha: /subvenciones/{codigo}-{slug} (o /en/grants/...). */
export const grantDetailPath = (
  lang: "es" | "en",
  s: { codigo: string; titulo?: string | null },
): string => {
  const slug = grantSlugFromTitulo(s.titulo);
  const segment = slug ? `${s.codigo}-${slug}` : s.codigo;
  return `${grantsBase(lang)}/${segment}`;
};

/**
 * Extrae el código de convocatoria del segmento de URL.
 * Acepta "AND-CONC-001" y "AND-CONC-001-ayudas-conciliacion-...".
 */
export const codigoFromParam = (param?: string | null): string => {
  const raw = (param ?? "").trim();
  if (!raw) return "";
  const m = raw.match(/^([A-Za-z]{2,6}-[A-Za-z]{2,10}-\d{1,5})/);
  return m?.[1] ? m[1].toUpperCase() : raw;
};

// ── Hub ──────────────────────────────────────────────────────────────────
export const hubHead = (lang: "es" | "en") =>
  lang === "es"
    ? {
        title: "Buscador de Subvenciones para Comercio y Retail | Retail Lemon",
        description:
          "Encuentra ayudas públicas, subvenciones de digitalización y fondos para el comercio minorista y restauración.",
        h1: "Subvenciones y financiación para tu comercio local",
        keywords:
          "alertas subvenciones email, notificaciones ayudas comercio españa, suscripcion subvenciones retail, avisos subvenciones pymes 2026, recibir alertas por correo",
      }
    : {
        title: "Retail Grants & Subsidies Finder | Retail Lemon",
        description:
          "Discover government grants, funding, and digitization subsidies tailored for retail businesses.",
        h1: "Grants and funding for your local business",
        keywords:
          "email alerts retail grants, Spain grants notifications, retail subsidies 2026, SME grants Spain, subscribe to business grants",
      };

// ── Directorio por comunidad ─────────────────────────────────────────────
export const territoryHead = (lang: "es" | "en", comunidad: string) =>
  lang === "es"
    ? {
        title: `Subvenciones y Ayudas para Comercios en ${comunidad} 2026 | Retail Lemon`,
        description: `Consulta todas las ayudas y subvenciones abiertas para comercios y pymes en ${comunidad}. Requisitos, plazos e importes actualizados.`,
        h1: `Subvenciones y Ayudas a Fondo Perdido en ${comunidad}`,
        keywords: `subvenciones comercios ${comunidad}, ayudas pequeñas empresas ${comunidad}, ayudas a fondo perdido ${comunidad}, plazo solicitud ayudas`,
      }
    : {
        title: `Grants and Subsidies for Retail Businesses in ${comunidad} 2026 | Retail Lemon`,
        description: `All open grants and subsidies for shops and SMEs in ${comunidad}. Requirements, deadlines and amounts, kept up to date.`,
        h1: `Non-repayable Grants and Subsidies in ${comunidad}`,
        keywords: `retail grants ${comunidad}, small business grants ${comunidad}, application deadline grants`,
      };

// ── Directorio por sector ────────────────────────────────────────────────
export const sectorHead = (lang: "es" | "en", sector: string) =>
  lang === "es"
    ? {
        title: `Subvenciones y Ayudas para ${sector} 2026 | Retail Lemon`,
        description: `Listado de ayudas a fondo perdido y financiación para negocios de ${sector}. Descubre convocatorias abiertas e importes disponibles.`,
        h1: `Subvenciones y Ayudas para Negocios de ${sector}`,
        keywords: `subvenciones ${sector} 2026, ayudas ${sector}, ayudas digitalización comercio minorista, requisitos subvención`,
      }
    : {
        title: `Grants and Subsidies for ${sector} 2026 | Retail Lemon`,
        description: `List of non-repayable grants and funding for ${sector} businesses. Discover open calls and available amounts.`,
        h1: `Grants and Subsidies for ${sector} Businesses`,
        keywords: `${sector} grants 2026, retail digitalisation aid, grant requirements`,
      };

// ── Ficha técnica ────────────────────────────────────────────────────────
export const grantDetailHead = (
  lang: "es" | "en",
  row: { titulo: string; organismo?: string | null; ambito?: string | null; importe?: string | null },
) => {
  const organismo = row.organismo ?? (lang === "es" ? "la administración competente" : "the awarding body");
  const ambito = row.ambito ?? (lang === "es" ? "España" : "Spain");
  const importe = row.importe ?? (lang === "es" ? "importe según convocatoria" : "amount per official call");
  return lang === "es"
    ? {
        title: `${row.titulo} | Ayudas y Subvenciones 2026`,
        description: `Información oficial de la subvención ${row.titulo}: importe de ${importe}, ámbito ${ambito}, organismo ${organismo}. Consulta plazos y requisitos.`,
      }
    : {
        title: `${row.titulo} | Grants and Subsidies 2026`,
        description: `Official information for the ${row.titulo} grant: amount ${importe}, scope ${ambito}, awarding body ${organismo}. Check deadlines and requirements.`,
      };
};

export const grantJsonLd = (
  row: {
    codigo: string;
    titulo: string;
    organismo?: string | null;
    ambito?: string | null;
    importe?: string | null;
    beneficiarios?: string | null;
    categoria?: string | null;
  },
  url: string,
  lang: "es" | "en",
): Record<string, unknown> => {
  const organismo = row.organismo ?? (lang === "es" ? "Administración Pública" : "Public Administration");
  const ambito = row.ambito ?? (lang === "es" ? "España" : "Spain");
  const importe = row.importe ?? "";
  return {
    "@context": "https://schema.org",
    "@type": "GovernmentService",
    name: row.titulo,
    serviceType: row.categoria ?? (lang === "es" ? "Subvención" : "Grant"),
    serviceOperator: { "@type": "GovernmentOrganization", name: organismo },
    provider: { "@type": "GovernmentOrganization", name: organismo },
    areaServed: ambito,
    audience: {
      "@type": "Audience",
      audienceType: row.beneficiarios ?? (lang === "es" ? "Comercios y pymes" : "Shops and SMEs"),
    },
    offers: { "@type": "Offer", price: importe, priceCurrency: "EUR" },
    description:
      lang === "es"
        ? `Subvención ${row.titulo} otorgada por ${organismo} para el ámbito de ${ambito}${importe ? ` con un importe de ${importe}` : ""}.`
        : `Grant ${row.titulo} awarded by ${organismo} for ${ambito}${importe ? ` with an amount of ${importe}` : ""}.`,
    url,
  };
};

// ── Resolución de slugs desde datos de la ficha ───────────────────────────
/** Comunidad autónoma detectada en el ámbito (null si es nacional/UE o desconocida). */
export const comunidadSlugFromAmbito = (
  ambito?: string | null,
): { slug: string; name: string } | null => {
  if (!ambito || isAmbitoGlobal(ambito)) return null;
  const hit = COMUNIDAD_SLUGS.find((c) => matchesComunidad(ambito, c.name) && !isAmbitoGlobal(ambito));
  return hit ?? null;
};

/** Slug del sector a partir de su etiqueta del monitor. */
export const sectorSlugFromLabel = (label?: string | null): { slug: string; label: string } | null => {
  if (!label) return null;
  const s = slugify(label);
  return SECTOR_SLUGS.find((x) => x.slug === s) ?? null;
};
